import { create } from 'zustand';

// Shared voice status, so UIs that mount later (the in-headset VR menu only
// exists while an XR session runs) read the current state instead of waiting
// for the next window event. VoiceChatWrapper / VoiceChat write it.
export type VoicePhase =
  | 'unavailable' // no voice component mounted (signed out)
  | 'idle'        // can join, not joined
  | 'joining'     // fetching a token
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

interface VoiceState {
  phase: VoicePhase;
  muted: boolean;
  error: string | null;
  /** Microphone permission was refused (browser or headset setting). */
  micBlocked: boolean;
  /** Connected, but the browser has not allowed remote audio to play yet. */
  needsAudioUnlock: boolean;
  /** Number of mounted VoiceChatWrappers (the editor can mount two). */
  mounts: number;
  /** A join requested before any VoiceChatWrapper mounted (e.g. Enter VR
   *  tapped while the world was still loading); the next wrapper takes it. */
  pendingJoin: { muted: boolean } | null;
  /** Someone in the voice room (you included) is talking right now (LiveKit
   *  active speakers). World music ducks under it. */
  anySpeaking: boolean;
}

export const useVoiceStore = create<VoiceState>(() => ({
  phase: 'unavailable',
  muted: true,
  error: null,
  micBlocked: false,
  needsAudioUnlock: false,
  mounts: 0,
  pendingJoin: null,
  anySpeaking: false,
}));

export const setVoiceStatus = (patch: Partial<VoiceState>) => useVoiceStore.setState(patch);

export const isMicPermissionError = (err: unknown) => {
  const e = err as { name?: string; message?: string } | null;
  return !!e && (e.name === 'NotAllowedError' || e.name === 'SecurityError' || /permission|denied|not allowed/i.test(e.message || ''));
};
export const MIC_BLOCKED_MESSAGE = 'Mic blocked — allow microphone in browser settings';

/** Join the voice room. Must be called from a user gesture where possible
 *  (it unlocks audio playback). `muted: false` opens the mic once connected. */
export function requestVoiceJoin(opts: { muted: boolean }) {
  if (!useVoiceStore.getState().mounts) { useVoiceStore.setState({ pendingJoin: opts }); return; }
  window.dispatchEvent(new CustomEvent('gathrxr:voice-join', { detail: opts }));
}

/** Retry remote-audio playback (AudioContext / <audio> autoplay unlock). */
export function unlockVoiceAudio() {
  window.dispatchEvent(new CustomEvent('gathrxr:voice-unlock'));
}

/** The VR menu's mic button: joins if not in the room yet, otherwise toggles
 *  mute. Returns false when voice isn't available (signed out). */
export function pressVoiceButton(): boolean {
  const s = useVoiceStore.getState();
  unlockVoiceAudio();
  switch (s.phase) {
    case 'unavailable':
      return false;
    case 'idle':
    case 'error':
      requestVoiceJoin({ muted: false });
      return true;
    case 'connected':
      window.dispatchEvent(new CustomEvent('toggleMute'));
      return true;
    default: // joining / connecting / reconnecting: already on the way
      return true;
  }
}

/** Short label for the VR mic button. */
export function voiceButtonLabel(s: Pick<VoiceState, 'phase' | 'muted' | 'micBlocked'>): { label: string; hint: string } {
  if (s.micBlocked) return { label: 'Mic blocked', hint: 'Allow mic in browser settings' };
  switch (s.phase) {
    case 'unavailable': return { label: 'Voice off', hint: 'Sign in to talk' };
    case 'idle': return { label: 'Join voice', hint: 'Talk with people here' };
    case 'joining':
    case 'connecting': return { label: 'Connecting…', hint: 'Joining voice' };
    case 'reconnecting': return { label: 'Reconnecting…', hint: 'Voice dropped' };
    case 'error': return { label: 'Voice failed', hint: 'Tap to try again' };
    case 'connected': return s.muted
      ? { label: 'Muted', hint: 'Tap to talk' }
      : { label: 'Mic on', hint: 'Tap to mute' };
  }
}
